import { CommonIP, CommonIPLite, CommonIPState, DEFAULT_IP_STATE, IP, WsFile } from '@model/ftf-api.model';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import * as ItemsActions from '../actions/items.actions';

export interface ItemsState extends EntityState<CommonIP> {
  error: unknown;
}

/**
 * The entity adapter for managing CommonIP entities.
 */
export const adapter: EntityAdapter<CommonIP> = createEntityAdapter<CommonIP>({
  selectId: (item: CommonIP) => itemKey(item),
});

/**
 * The initial state for the items feature.
 */
export const initialState: ItemsState = adapter.getInitialState({
  error: null,
});

/**
 * Finds which trifold contains a document with the specified ID
 */
const findTrifoldForDocument = (trifoldData: Record<IP.TrifoldEnum, WsFile[]>, docId: number): IP.TrifoldEnum | null => {
  for (const trifold of [IP.TrifoldEnum.L, IP.TrifoldEnum.M, IP.TrifoldEnum.R]) {
    if (trifoldData[trifold].some((doc) => doc.fileId === docId)) {
      return trifold;
    }
  }
  return null;
};

/**
 * Creates a compound key from the identifier and ipType of an item.
 *
 * @param item - The item to create the compound key for.
 * @returns The compound key as a string.
 */
export const itemKey = (item: CommonIPLite): string => `${item.ipType}|${item.identifier}`;

/**
 * Helper reducer function that finds a particular item in the state and updates it
 *   via provided updateFn callback.
 *
 * @param state - The current state of items.
 * @param item - The item to be updated, identified by its identifier and ipType.
 * @param updateFn - A function that takes an item and returns the updated item.
 * @returns The new state with the updated item.
 */
const updateItemInStateReducer = (state: ItemsState, item: CommonIPLite, updateFn: (i: CommonIP) => CommonIP): ItemsState => {
  const id = itemKey(item);
  const entity = state.entities[id];
  // if entity doesn't exist then bailout
  if (!entity) return state;
  // update it
  return adapter.updateOne({ id, changes: updateFn(entity) }, state);
};

/**
 * The reducer function for the items feature.
 */
export const itemsReducer = createReducer(
  initialState,
  on(ItemsActions.addItemSuccess, (state, { item }) => {
    const id = itemKey(item);

    // Minimize all other items
    const minimizedState = adapter.map(
      (entity) => ({
        ...entity,
        state: { ...(entity.state ?? DEFAULT_IP_STATE), expanded: false },
      }),
      state
    );

    // determine if the item is already in the state
    const exists = (state.ids as string[]).includes(id);
    const existingEntity = state.entities[id];
    const existingState = existingEntity?.state ?? DEFAULT_IP_STATE;
    const incomingState: Partial<CommonIPState> = item.state ?? {};
    const mergedState = exists
      ? {
        ...existingState,
        view: 'trifold' as const,
        trifoldFilters: incomingState.trifoldFilters?.docSubtypeId?.length
          ? incomingState.trifoldFilters
          : existingState.trifoldFilters,
        expanded: true,
      }
      : {
        ...DEFAULT_IP_STATE,
        ...incomingState,
        expanded: true,
      };
    const newRecord = {
      ...item,
      state: mergedState,
    };
    // Add / update the new item and set its expanded state to true
    const newState = exists ? adapter.updateOne({ id, changes: newRecord }, minimizedState) : adapter.addOne(newRecord, minimizedState);

    // Prepend the new ID to the list of IDs
    const ids = [id, ...(newState.ids.filter((existingId) => existingId !== id) as string[])];

    return {
      ...newState,
      ids,
    };
  }),
  on(ItemsActions.removeItem, (state, { item }) => adapter.removeOne(itemKey(item), state)),
  on(ItemsActions.updateItem, (state, { item, skipState }) =>
    updateItemInStateReducer(state, item, (i) => {
      // add in the changes to the item and optionally skips updating the state property
      // by keeping the old value
      const itemState = i.state;
      return skipState ? { ...i, ...item, state: itemState } : { ...i, ...item };
    })
  ),
  on(ItemsActions.updateItemState, (state, { item, itemState }) =>
    updateItemInStateReducer(state, item, (i) => {
      const updatedState: CommonIPState = {
        // existing state
        ...(i.state ?? DEFAULT_IP_STATE),
        // updated state properties
        ...itemState,
      };
      // but keep the rest of the item the same
      return { ...i, state: updatedState };
    })
  ),
  on(ItemsActions.loadItemsSuccess, (state, { items }): ItemsState => adapter.setAll(items, state)),
  // failure actions collapsed into common reducer for simplicity
  on(ItemsActions.addItemFailure, ItemsActions.loadItemsFailure, (state, { error }): ItemsState => ({ ...state, error })),
  on(ItemsActions.updateItemTrifoldDocument, (state, { item, trifold, id, doc }) =>
    updateItemInStateReducer(state, item, (i) => {
      // if there is no trifold data, return the item as is.. can't update it state
      if (!i.trifold) return i;

      // Determine which trifold contains the document
      const targetTrifold = trifold || findTrifoldForDocument(i.trifold, id);

      // Early return if we couldn't find the document
      if (!targetTrifold) return i;

      // Update the trifold data immutably
      return {
        ...i,
        trifold: {
          ...i.trifold,
          [targetTrifold]: i.trifold[targetTrifold].map((curDoc) => (curDoc.fileId === id ? { ...curDoc, ...doc } : curDoc)),
        },
      };
    })
  ),
  on(ItemsActions.updateItemWorkflow, (state, { item, id, workflow }) =>
    updateItemInStateReducer(state, item, (i) => {
      // if there is no workflow data, return the item as is.. can't update it state
      if (!i.detail?.workflow) return i;
      // only update the workflow with the matching id
      const updatedWorkflows = i.detail?.workflow.map((cur) => (cur.id === id ? { ...cur, ...workflow } : cur));

      return {
        ...i,
        detail: {
          ...i.detail,
          // sub in the changed workflows
          workflow: updatedWorkflows,
        },
      };
    })
  ),
  // Reset state
  on(ItemsActions.resetItems, () => initialState)
);

// get the selectors
const { selectAll, selectEntities } = adapter.getSelectors();

// select the array of users
export const selectAllItems = selectAll;
// select the entities dictionary
export const selectItemEntities = selectEntities;
// select the current error
export const selectItemsError = (state: ItemsState) => state.error;
